import { cloneElement, useId, useState, type ReactElement, type ReactNode } from 'react';

/**
 * The design-system tooltip (docs/ux/DESIGN.md: 11px on ink, after 400 ms; the icon rail's labels
 * in shell-nav.css are the same box) for an icon-only control: the text is the control's accessible
 * *description* (`aria-describedby`, the `aria-label` stays its name), shown on hover and on keyboard
 * focus by CSS, and dismissed with Escape (WCAG 1.4.13) until the pointer leaves or the focus moves.
 * No portal and no measurement: it is positioned under its wrapper, so it stays tiny — it ships in
 * the shell on every page (§4.5).
 */
export function Tooltip({ text, children, align = 'center', className, testId }: { text: ReactNode; children: ReactElement<{ 'aria-describedby'?: string }>; align?: 'center' | 'end'; className?: string; testId?: string }) {
  const id = useId();
  const [dismissed, setDismissed] = useState(false);
  return (
    <span
      className={`tip-wrap${className ? ` ${className}` : ''}`}
      data-align={align}
      data-dismissed={dismissed || undefined}
      onKeyDown={(e) => {
        if (e.key === 'Escape') setDismissed(true);
      }}
      onPointerLeave={() => setDismissed(false)}
      onBlur={() => setDismissed(false)}
    >
      {cloneElement(children, { 'aria-describedby': id })}
      <span role="tooltip" id={id} className="tip" data-testid={testId}>
        {text}
      </span>
    </span>
  );
}
